import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Eraser, ChevronDown, Loader2, RotateCcw, Send, CheckCircle, Eye, FileText, StickyNote, X,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchWriteoffVendors, fetchWriteoffPOs, fetchWriteoffInvoices, fetchWriteoffInvoiceTaxes,
    submitVendorPayableWriteoff,
    clearWriteoffVendors, clearWriteoffPOs, clearWriteoffInvoices, clearWriteoffTaxes,
    clearWriteoffSaveResult, resetVendorPayableWriteoff,
    selectWOVendors, selectWOPOs, selectWOInvoices, selectWOInvoicesLoaded, selectWOTaxes,
    selectWOLoading, selectWOErrors, selectWOSaveStatus, selectWOSaveRefNo, selectWOSaveError,
} from '../../slices/purchaseSlice/vendorPayableWriteoffSlice';

// Legacy /Purchase/VendorPayableWriteoff: vendor type → vendor → POs → View open invoices →
// tick invoices → date / remarks → Submit (SaveWriteoffPOInvoicePayment).

// ── Constants / helpers ────────────────────────────────────────────────────────

const VENDOR_TYPES = ['Service Provider', 'Supplier'];
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Date → "DD-Mon-YYYY" (legacy datepicker format 'dd-M-yy')
const formatDateForAPI = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const num = (v) => Number(v) || 0;
const fmt = (v) => num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// The save / invoice SPs walk these lists with WHILE charindex(',') — every value, including the
// last, must be followed by a comma (legacy builds them as `value + ','`).
const csv = (values) => values.map((v) => `${String(v ?? '').trim()},`).join('');

// Legacy marks an invoice "with tax" (red row, tax popup) when TaxAmt > 0.
const hasTax = (inv) => num(inv.TaxAmt) > 0;

const rowKey = (inv) => `${inv.InvoiceNo}|${inv.PONo}`;

// ── Shared UI ──────────────────────────────────────────────────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const readonlyCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-gray-50 dark:bg-gray-900 text-indigo-700 dark:text-indigo-300 font-semibold border-gray-300 dark:border-gray-600 cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const CardHeader = ({ icon: Icon, title, action }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
        </div>
        {action}
    </div>
);

const ChipGroup = ({ options, value, onChange, disabled }) => (
    <div className="flex gap-2">
        {options.map((opt) => (
            <button key={opt} type="button" disabled={disabled} onClick={() => onChange(opt)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                    value === opt
                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-indigo-300 dark:hover:border-indigo-700'
                }`}>
                {opt}
            </button>
        ))}
    </div>
);

// Checkbox dropdown with Select All (legacy bootstrap-multiselect, includeSelectAllOption).
const POMultiSelect = ({ options, selected, onChange, disabled, loading }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handleOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    const allSelected = options.length > 0 && selected.length === options.length;
    const toggle = (po) => onChange(selected.includes(po) ? selected.filter((p) => p !== po) : [...selected, po]);

    return (
        <div ref={ref} className="relative">
            <div role="button" tabIndex={disabled ? -1 : 0}
                onClick={() => !disabled && setOpen((o) => !o)}
                onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen((o) => !o); } }}
                className={`${inputCls} min-h-[38px] pr-8 flex items-center relative ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                <span className={selected.length ? 'text-gray-800 dark:text-gray-100 truncate' : 'text-gray-400'}>
                    {selected.length === 0 ? '-Select POs-'
                        : allSelected ? `All POs (${selected.length})`
                        : selected.length <= 2 ? selected.join(', ')
                        : `${selected.length} POs selected`}
                </span>
                <SelectIcon loading={loading} />
            </div>
            {open && (
                <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg divide-y divide-gray-100 dark:divide-gray-700">
                    <label className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-indigo-700 dark:text-indigo-300 cursor-pointer hover:bg-indigo-50/60 dark:hover:bg-indigo-900/20">
                        <input type="checkbox" className="h-4 w-4 accent-indigo-600" checked={allSelected}
                            onChange={() => onChange(allSelected ? [] : options.map((o) => o.PONo))} />
                        Select All
                    </label>
                    {options.map((po) => (
                        <label key={po.PONo}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-indigo-50/60 dark:hover:bg-indigo-900/20">
                            <input type="checkbox" className="h-4 w-4 accent-indigo-600"
                                checked={selected.includes(po.PONo)} onChange={() => toggle(po.PONo)} />
                            {po.PONoDesc || po.PONo}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────────

const VendorPayableWriteoff = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const vendors        = useSelector(selectWOVendors);
    const pos            = useSelector(selectWOPOs);
    const invoices       = useSelector(selectWOInvoices);
    const invoicesLoaded = useSelector(selectWOInvoicesLoaded);
    const taxes          = useSelector(selectWOTaxes);
    const loading        = useSelector(selectWOLoading);
    const errors         = useSelector(selectWOErrors);
    const saveStatus     = useSelector(selectWOSaveStatus);
    const saveRefNo      = useSelector(selectWOSaveRefNo);
    const saveError      = useSelector(selectWOSaveError);

    const roleId    = Number(userData?.roleId || userData?.RID || 0);
    const userId    = Number(userData?.UID || userData?.userId || 0);
    const createdBy = String(userData?.userName || userData?.employeeId || '');

    const [vendorType,  setVendorType]  = useState('');
    const [vendorCode,  setVendorCode]  = useState('');
    const [selectedPOs, setSelectedPOs] = useState([]);
    const [viewed,      setViewed]      = useState(false);   // filters locked after View (legacy disables them)
    const [checked,     setChecked]     = useState({});      // rowKey → true
    const [date,        setDate]        = useState(() => new Date());
    const [remarks,     setRemarks]     = useState('');
    const [taxInvoice,  setTaxInvoice]  = useState(null);
    const [submitted,   setSubmitted]   = useState(false);

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => () => { dispatch(resetVendorPayableWriteoff()); }, [dispatch]);

    useEffect(() => {
        setVendorCode('');
        setSelectedPOs([]);
        dispatch(clearWriteoffPOs());
        if (vendorType) dispatch(fetchWriteoffVendors({ VendorType: vendorType, RoleId: roleId, Userid: userId }));
        else dispatch(clearWriteoffVendors());
    }, [vendorType, roleId, userId, dispatch]);

    useEffect(() => {
        setSelectedPOs([]);
        if (vendorType && vendorCode) dispatch(fetchWriteoffPOs({ VendorCode: vendorCode, VendorType: vendorType }));
        else dispatch(clearWriteoffPOs());
    }, [vendorCode, vendorType, dispatch]);

    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success(`Transaction Ref No. ${saveRefNo} Created Successfully`);
            setSubmitted(true);
        } else if (saveStatus === 'failed') {
            toast.error(saveError || 'Error Occurred While Adding New Payment');
            dispatch(clearWriteoffSaveResult());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    // ── Derived ───────────────────────────────────────────────────────────────

    const selectedInvoices = useMemo(() => invoices.filter((inv) => checked[rowKey(inv)]), [invoices, checked]);
    const totalAmount = useMemo(() => selectedInvoices.reduce((a, inv) => a + num(inv.TotalBalance), 0), [selectedInvoices]);
    const allChecked = invoices.length > 0 && selectedInvoices.length === invoices.length;

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleView = () => {
        const errs = [];
        if (!vendorType) errs.push('Select Vendor Type');
        if (!vendorCode) errs.push('Select Vendor');
        if (!selectedPOs.length) errs.push('Select PO Number');
        if (errs.length) { toast.warn(errs.join(', ')); return; }
        setChecked({});
        setViewed(true);
        dispatch(fetchWriteoffInvoices({ VendorCode: vendorCode, PONo: csv(selectedPOs) }));
    };

    // Legacy re-enables the filters when the grid comes back empty.
    useEffect(() => {
        if (viewed && invoicesLoaded && !loading.invoices && invoices.length === 0) setViewed(false);
    }, [viewed, invoicesLoaded, loading.invoices, invoices.length]);

    const handleReset = () => {
        setVendorType(''); setVendorCode(''); setSelectedPOs([]);
        setViewed(false); setChecked({}); setDate(new Date()); setRemarks(''); setTaxInvoice(null);
        setSubmitted(false);
        dispatch(resetVendorPayableWriteoff());
    };

    const toggleRow = (inv) => setChecked((p) => ({ ...p, [rowKey(inv)]: !p[rowKey(inv)] }));
    const toggleAll = () => setChecked(allChecked ? {} : Object.fromEntries(invoices.map((inv) => [rowKey(inv), true])));

    const openTaxes = (invoiceNo) => {
        setTaxInvoice(invoiceNo);
        dispatch(fetchWriteoffInvoiceTaxes({ InvoiceNo: invoiceNo }));
    };
    const closeTaxes = () => { setTaxInvoice(null); dispatch(clearWriteoffTaxes()); };

    const handleSubmit = () => {
        const errs = [];
        if (!selectedInvoices.length) errs.push('Select writeoff Invoice Numbers');
        if (!(date instanceof Date)) errs.push('Enter Date');
        if (!remarks.trim()) errs.push('Enter Remarks');
        if (!(totalAmount > 0)) errs.push('Enter Total Valid Amount');
        if (errs.length) { toast.warn(errs.join(', ')); return; }

        // Field → SP parameter mapping follows the Web API data layer (SaveWriteoffPOInvoicePayment).
        // "Basics" is the invoice BALANCE column (legacy reads td.eq(6)), which the SP deducts from VendorInvoice.Balance.
        const payload = {
            InvoiceNos:        csv(selectedInvoices.map((i) => i.InvoiceNo)),
            PONumbers:         csv(selectedInvoices.map((i) => i.PONo)),
            Basics:            csv(selectedInvoices.map((i) => num(i.InvoiceBalance))),
            Retentions:        csv(selectedInvoices.map((i) => num(i.RetentionBalance))),
            Holds:             csv(selectedInvoices.map((i) => num(i.HoldBalance))),
            InvTotals:         csv(selectedInvoices.map((i) => num(i.TotalBalance))),
            TaxApplicables:    csv(selectedInvoices.map((i) => (hasTax(i) ? 'Yes' : 'No'))),
            CCCodes:           csv(selectedInvoices.map((i) => i.CCCode)),
            TransactionDate:   formatDateForAPI(date),
            TransactionAmount: totalAmount.toFixed(2),
            Remarks:           remarks.trim(),
            VendorCode:        vendorCode,
            Roleid:            roleId,
            Createdby:         createdBy,
        };
        dispatch(submitVendorPayableWriteoff(payload));
    };

    // ── Success screen ────────────────────────────────────────────────────────

    if (submitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6 flex items-center justify-center">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-10 max-w-sm w-full text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-5 shadow-lg">
                        <CheckCircle className="h-8 w-8 text-white" />
                    </div>
                    <h2 className="text-xl font-black text-gray-900 dark:text-white mb-2">Write-off Submitted</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                        Transaction Ref No. <span className="font-bold text-indigo-600 dark:text-indigo-400">{saveRefNo}</span> created successfully.
                    </p>
                    <button onClick={handleReset}
                        className="w-full py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all">
                        New Entry
                    </button>
                </div>
            </div>
        );
    }

    // ── Render ────────────────────────────────────────────────────────────────

    const noPOs = vendorCode && !loading.pos && !errors.pos && pos.length === 0;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* Header */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Eraser className="h-5 w-5 text-white" />
                    </div>
                    <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                        {menuData?.name || 'Vendor Payable Write-off'}
                    </h1>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-4">

                {/* Filters */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <CardHeader icon={FileText} title="Vendor & POs" />
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                        <div>
                            <Label required>Vendor Type</Label>
                            <ChipGroup options={VENDOR_TYPES} value={vendorType} onChange={setVendorType} disabled={viewed} />
                        </div>
                        <div>
                            <Label required>Vendor</Label>
                            <div className="relative">
                                <select className={`${inputCls} appearance-none pr-8`} value={vendorCode}
                                    disabled={!vendorType || viewed || loading.vendors}
                                    onChange={(e) => setVendorCode(e.target.value)}>
                                    <option value="">-Please Select-</option>
                                    {vendors.map((v) => <option key={v.VendorCode} value={v.VendorCode}>{v.VendorName}</option>)}
                                </select>
                                <SelectIcon loading={loading.vendors} />
                            </div>
                            {errors.vendors && <p className="mt-1 text-xs text-rose-500">{errors.vendors}</p>}
                        </div>
                        <div>
                            <Label required>PO Number</Label>
                            <POMultiSelect options={pos} selected={selectedPOs} onChange={setSelectedPOs}
                                disabled={!vendorCode || viewed || loading.pos || noPOs} loading={loading.pos} />
                            {noPOs && <p className="mt-1 text-xs text-rose-500">PO Does Not Exist</p>}
                            {errors.pos && <p className="mt-1 text-xs text-rose-500">{errors.pos}</p>}
                        </div>
                        <div className="flex gap-2">
                            <button type="button" onClick={handleView} disabled={viewed || loading.invoices || noPOs}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold shadow-sm hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed">
                                {loading.invoices ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} View
                            </button>
                            <button type="button" onClick={handleReset}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700">
                                <RotateCcw className="h-4 w-4" /> Reset
                            </button>
                        </div>
                    </div>
                </div>

                {/* Invoice grid */}
                {invoicesLoaded && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={FileText} title={`Invoices (${selectedInvoices.length} of ${invoices.length} selected)`} />
                        {errors.invoices ? (
                            <p className="p-4 text-sm text-rose-500">{errors.invoices}</p>
                        ) : invoices.length === 0 ? (
                            <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No Data Found</p>
                        ) : (
                            <>
                                <div className="px-4 pt-3 text-xs font-semibold space-y-0.5">
                                    <p className="text-emerald-600 dark:text-emerald-400">Green rows: invoices without tax amount.</p>
                                    <p className="text-rose-600 dark:text-rose-400">Red rows: invoices with tax amount. Click the invoice no to see the tax values.</p>
                                </div>
                                <div className="p-4 overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                                <th className="px-2 py-2 text-center">
                                                    <input type="checkbox" className="h-4 w-4 accent-indigo-600" checked={allChecked} onChange={toggleAll} />
                                                </th>
                                                <th className="px-2 py-2 text-left">Invoice Date</th>
                                                <th className="px-2 py-2 text-left">Invoice No</th>
                                                <th className="px-2 py-2 text-left">PO No</th>
                                                <th className="px-2 py-2 text-left">Cost/Job Center</th>
                                                <th className="px-2 py-2 text-right">Basic</th>
                                                <th className="px-2 py-2 text-right">Invoice Balance</th>
                                                <th className="px-2 py-2 text-right">Retention Balance</th>
                                                <th className="px-2 py-2 text-right">Hold Balance</th>
                                                <th className="px-2 py-2 text-right">Total Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {invoices.map((inv) => {
                                                const tax = hasTax(inv);
                                                const color = tax ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400';
                                                const on = !!checked[rowKey(inv)];
                                                return (
                                                    <tr key={rowKey(inv)} className={`${color} ${on ? 'bg-indigo-50/60 dark:bg-indigo-900/20' : ''}`}>
                                                        <td className="px-2 py-2 text-center">
                                                            <input type="checkbox" className="h-4 w-4 accent-indigo-600" checked={on} onChange={() => toggleRow(inv)} />
                                                        </td>
                                                        <td className="px-2 py-2 whitespace-nowrap">{inv.InvoiceDate}</td>
                                                        <td className="px-2 py-2 font-semibold">
                                                            {tax ? (
                                                                <button type="button" onClick={() => openTaxes(inv.InvoiceNo)} className="underline hover:opacity-80">{inv.InvoiceNo}</button>
                                                            ) : inv.InvoiceNo}
                                                        </td>
                                                        <td className="px-2 py-2">{inv.PONo}</td>
                                                        <td className="px-2 py-2">{inv.CCCode}</td>
                                                        <td className="px-2 py-2 text-right">{fmt(inv.BasicValue)}</td>
                                                        <td className="px-2 py-2 text-right">{fmt(inv.InvoiceBalance)}</td>
                                                        <td className="px-2 py-2 text-right">{fmt(inv.RetentionBalance)}</td>
                                                        <td className="px-2 py-2 text-right">{fmt(inv.HoldBalance)}</td>
                                                        <td className="px-2 py-2 text-right font-semibold">{fmt(inv.TotalBalance)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Details + submit (legacy shows these once the grid has rows) */}
                {invoicesLoaded && invoices.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={StickyNote} title="Write-off Details" />
                        <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                                <Label required>Date</Label>
                                <CustomDatePicker value={date} onChange={setDate} format="DD-MMM-YYYY" maxDate={new Date()} placeholder="Select date" />
                            </div>
                            <div className="md:col-span-2">
                                <Label required>Remarks</Label>
                                <textarea rows={3} className={inputCls} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                            </div>
                            <div>
                                <Label>Total Amount</Label>
                                <input type="text" readOnly className={readonlyCls} value={`₹ ${fmt(totalAmount)}`} />
                            </div>
                        </div>
                        <div className="px-4 pb-4 flex justify-end">
                            <button type="button" onClick={handleSubmit} disabled={loading.save}
                                className="inline-flex items-center gap-1.5 px-6 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow-md hover:from-indigo-700 hover:to-purple-700 disabled:opacity-60 disabled:cursor-not-allowed">
                                {loading.save ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Tax popup — portaled so it sits above app chrome */}
            {taxInvoice && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/50 p-4" onClick={closeTaxes}>
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Invoice {taxInvoice} — Tax / Deductions</h3>
                            <button type="button" onClick={closeTaxes} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-4 overflow-auto">
                            {loading.taxes ? (
                                <p className="flex items-center gap-2 text-sm text-indigo-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
                            ) : errors.taxes ? (
                                <p className="text-sm text-rose-500">{errors.taxes}</p>
                            ) : taxes.length === 0 ? (
                                <p className="text-sm text-gray-500">No tax lines.</p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                            {['Cost/Job Center', 'Account Head', 'Sub Account Head', 'RegNo', 'Tax Type', 'Type', 'Amount', 'Balance'].map((h, i) => (
                                                <th key={h} className={`px-2 py-2 ${i >= 6 ? 'text-right' : 'text-left'}`}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-gray-700 dark:text-gray-200">
                                        {taxes.map((t, i) => (
                                            <tr key={i}>
                                                <td className="px-2 py-2">{t.CCCode}</td>
                                                <td className="px-2 py-2">{t.DCACode}</td>
                                                <td className="px-2 py-2">{t.SubDCACode}</td>
                                                <td className="px-2 py-2">{t.TaxNo}</td>
                                                <td className="px-2 py-2">{t.TaxType}</td>
                                                <td className="px-2 py-2">{t.Type}</td>
                                                <td className="px-2 py-2 text-right">{fmt(t.Amount)}</td>
                                                <td className="px-2 py-2 text-right">{fmt(t.Balance)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default VendorPayableWriteoff;
